import * as React from 'react';
import { ONE_YEAR_MILLISECONDS } from 'utils/timeUtils';
import { UserProperty } from 'utils/analytics/eventTypesConstants';
import { currentMetroAreaSelector } from 'selectors/metroAreaSelectors';
import { customDimensions } from 'utils/Analytics';
import { googleAnalyticsScriptsLoadedSelector } from 'selectors/cookieConsentSelectors';
import { setPatronLocationCookie } from 'utils/patronLocationCookieUtils';
import { useSelector } from 'react-redux';

/**
 * Pure logic component that reacts to the city picker changing. This is
 * logically part of the city picker but should be installed at the root of the
 * document, and only once per app.
 */
const MetroAreaStateMonitor: React.FunctionComponent<{}> = () => {
  const metroArea = useSelector(currentMetroAreaSelector);
  const isGaScriptLoaded = useSelector(googleAnalyticsScriptsLoadedSelector);

  React.useEffect(() => {
    const property: UserProperty = { property: 'Metro area', value: metroArea?.name || '' };
    // Don't set the dimension until after we're sure that GA scripts have been loaded.
    // NOTE(jacob, 2024-07-29): We're blocking both Amplitude and GA events on
    // the GA script loading. This isn't ideal but there's no current way in consumer to detect
    // Amplitude script loading. Prior to the change, we were firing the Amplitude event without
    // waiting for anything, which would result in the event getting missed entirely if we called
    // this code before the Amplitude script loaded, so this seems like at least an improvement over
    // that.
    if (!isGaScriptLoaded) {
      return;
    }
    window?.analytics?.setUserProperty(property);
    window?.analytics?.setDimensionInTockOnly(
      customDimensions.SELECTED_METRO_AREA,
      metroArea?.name ?? ''
    );
  }, [metroArea, isGaScriptLoaded]);

  React.useEffect(() => {
    if (window && window.location) {
      setPatronLocationCookie(window.location.hostname, metroArea?.id ?? '', ONE_YEAR_MILLISECONDS);
    }
  }, [metroArea]);

  return null;
};

export default MetroAreaStateMonitor;
